import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../../api/httpClient";
import { buildAssetUrl, getApiBaseUrl } from "../../data/authStorage";
import { createInitialsAvatar } from "../../utils/initialsAvatar";
import StaffIdCard from "../../components/employee/StaffIdCard";
import { InviteModal } from "../../components/modal";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import { getAdminEmployeeWallet } from "../../api/wallet";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";

const sections = [
  "Basic Info",
  "Organization",
  "Tools",
  "ID Card",
  "Education",
  "Experience",
  "Awards & Certification",
  "Employment & Benefits",
  "Wallet",
  "Actions",
];

const formatDate = (value) => {
  if (!value) return "-";

  const nextDate = new Date(value);
  if (Number.isNaN(nextDate.getTime())) {
    return value;
  }

  return nextDate.toLocaleDateString();
};

const renderCollection = (items, emptyText, renderer) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-sm text-gray-500">{emptyText}</p>;
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={`${index}-${JSON.stringify(item)}`} className="rounded-lg border border-gray-100 p-3 text-sm text-[#071C50]">
          {renderer(item, index)}
        </div>
      ))}
    </div>
  );
};

const labelValue = (label, value) => (
  <p className="text-sm text-[#071C50]">
    <span className="font-medium">{label}: </span>
    {value || "-"}
  </p>
);

const normalizeText = (value) => {
  if (value === null || value === undefined) {
    return "-";
  }

  const text = `${value}`.trim();
  return text || "-";
};

const formatMoney = (kobo = 0, currency = "NGN") =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(kobo || 0) / 100);

const EmployeesPage = () => {
  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get("id") || "";
  const apiBaseUrl = getApiBaseUrl();
  const navigate = useNavigate();
  const { companyAccount: organizationAccount, currentEmployee } = useCurrentEmployee();
  const companyAccount = organizationAccount || currentEmployee?.modulePermissions?.includes("employees");

  const [activeSection, setActiveSection] = useState("Basic Info");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [employee, setEmployee] = useState(null);
  const [onboardingOptions, setOnboardingOptions] = useState({
    departments: [],
    roles: [],
    levels: [],
    positions: [],
    branches: [],
  });

  const [offboardLoading, setOffboardLoading] = useState(false);
  const [offboardError, setOffboardError] = useState("");
  const [offboardSuccess, setOffboardSuccess] = useState("");
  const [noticePeriod, setNoticePeriod] = useState("");
  const [toolsAssigned, setToolsAssigned] = useState("");
  const [assignedTools, setAssignedTools] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [toolsError, setToolsError] = useState("");
  const [toolsSuccess, setToolsSuccess] = useState("");
  const [toolManagerOpen, setToolManagerOpen] = useState(false);
  const [toolManagerMode, setToolManagerMode] = useState("assign");
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const [toolSubmitting, setToolSubmitting] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState("");
  const [showEditForm, setShowEditForm] = useState(false);
  const [walletDetails, setWalletDetails] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState("");
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    officialEmail: "",
    personalEmail: "",
    phone: "",
    employmentType: "",
    gender: "",
    maritalStatus: "",
    stateOrigin: "",
    stateResidence: "",
    lga: "",
    residentialAddress: "",
    departmentId: "",
    departmentIds: [],
    roleId: "",
    levelId: "",
    positionId: "",
    branchId: "",
    dateJoined: "",
    employeeStatus: "Approved",
  });

  const syncEditForm = (nextEmployee) => {
    setEditForm({
      firstName: nextEmployee?.firstName || "",
      lastName: nextEmployee?.lastName || "",
      officialEmail: nextEmployee?.officialEmail || "",
      personalEmail: nextEmployee?.personalEmail || "",
      phone: nextEmployee?.phone || "",
      employmentType: nextEmployee?.employmentType || "",
      gender: nextEmployee?.gender || "",
      maritalStatus: nextEmployee?.maritalStatus || "",
      stateOrigin: nextEmployee?.stateOrigin || "",
      stateResidence: nextEmployee?.stateResidence || "",
      lga: nextEmployee?.lga || "",
      residentialAddress: nextEmployee?.residentialAddress || "",
      departmentId: nextEmployee?.department?.id || "",
      departmentIds: Array.isArray(nextEmployee?.departments)
        ? nextEmployee.departments.map((department) => department.id)
        : (nextEmployee?.department?.id ? [nextEmployee.department.id] : []),
      roleId: nextEmployee?.role?.id || "",
      levelId: nextEmployee?.level?.id || "",
      positionId: nextEmployee?.position?.id || "",
      branchId: nextEmployee?.branch?.id || "",
      dateJoined: nextEmployee?.dateJoined || "",
      employeeStatus: nextEmployee?.status || "Approved",
    });
  };

  useEffect(() => {
    const loadOnboardingOptions = async () => {
      if (!apiBaseUrl || !companyAccount) {
        return;
      }

      try {
        const response = await axios.get(`${apiBaseUrl}/organizations/onboarding/options`);
        const options = response?.data?.options || {};

        setOnboardingOptions({
          departments: options.departments || [],
          roles: options.roles || [],
          levels: options.levels || [],
          positions: options.positions || [],
          branches: options.branches || [],
        });
      } catch {
        // Keep defaults when options endpoint fails.
      }
    };

    loadOnboardingOptions();
  }, [apiBaseUrl, companyAccount]);

  useEffect(() => {
    const loadAvailableTools = async () => {
      if (!apiBaseUrl || !companyAccount) {
        return;
      }

      try {
        setToolsLoading(true);
        setToolsError("");

        const response = await axios.get(`${apiBaseUrl}/organizations/tools`);
        setAvailableTools(Array.isArray(response?.data?.tools) ? response.data.tools : []);
      } catch (requestError) {
        const message = requestError?.response?.data?.message || "Unable to load tools.";
        setToolsError(Array.isArray(message) ? message.join(", ") : message);
        setAvailableTools([]);
      } finally {
        setToolsLoading(false);
      }
    };

    loadAvailableTools();
  }, [apiBaseUrl, companyAccount]);

  useEffect(() => {
    const loadEmployee = async () => {
      if (!employeeId) {
        setError("No employee selected.");
        return;
      }

      if (!apiBaseUrl) {
        setError("API base URL is not configured.");
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await axios.get(`${apiBaseUrl}/organizations/employees/${encodeURIComponent(employeeId)}`);
        const nextEmployee = response?.data?.employee || null;

        const normalizedToolAssignments = Array.isArray(nextEmployee?.toolAssignments)
          ? nextEmployee.toolAssignments
              .filter((assignment) => assignment?.status === "Assigned")
              .map((assignment) => assignment?.tool?.name)
              .filter(Boolean)
          : [];

        setEmployee(nextEmployee);
        syncEditForm(nextEmployee);
        setNoticePeriod(nextEmployee?.offboardingNoticePeriod || "");
        setToolsAssigned(nextEmployee?.offboardingToolsAssigned || normalizedToolAssignments.join(", ") || "");
        setAssignedTools(normalizedToolAssignments);
      } catch (requestError) {
        const message = requestError?.response?.data?.message || "Unable to load employee details.";
        setError(Array.isArray(message) ? message.join(", ") : message);
      } finally {
        setLoading(false);
      }
    };

    loadEmployee();
  }, [apiBaseUrl, employeeId]);

  const profilePhoto = useMemo(() => {
    const path = employee?.profilePhotoUrl;
    const name = employee?.fullName || employee?.name || `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim();
    if (!path) {
      return createInitialsAvatar(name);
    }

    return buildAssetUrl(path) || createInitialsAvatar(name);
  }, [employee]);

  const employeeStatus = employee?.isOffboarded ? "Offboarded" : (employee?.status || "Approved");
  const statusColor = employeeStatus === "Offboarded" ? "text-[#FF383B]" : employeeStatus === "Pending" ? "text-[#F59E0B]" : "text-[#12CD18]";

  const assignedToolAssignments = useMemo(() => (
    Array.isArray(employee?.toolAssignments)
      ? employee.toolAssignments.filter((assignment) => assignment?.status === "Assigned")
      : []
  ), [employee?.toolAssignments]);

  const assignedToolIds = useMemo(
    () => assignedToolAssignments.map((assignment) => assignment?.tool?.id).filter(Boolean),
    [assignedToolAssignments],
  );

  const assignedToolLabels = useMemo(
    () => assignedToolAssignments.map((assignment) => assignment?.tool?.name).filter(Boolean),
    [assignedToolAssignments],
  );

  const filteredRoleOptions = useMemo(
    () => (onboardingOptions.roles || []).filter((role) => !editForm.departmentId || role.departmentId === editForm.departmentId),
    [editForm.departmentId, onboardingOptions.roles],
  );
  const visibleSections = useMemo(
    () =>
      sections.filter((section) =>
        companyAccount ? true : !["Tools", "Wallet", "Actions"].includes(section),
      ),
    [companyAccount],
  );

  useEffect(() => {
    if (!editForm.roleId) {
      return;
    }

    const selectedRole = (onboardingOptions.roles || []).find((role) => role.id === editForm.roleId);
    if (selectedRole && selectedRole.departmentId !== editForm.departmentId) {
      setEditForm((prev) => ({ ...prev, roleId: "" }));
    }
  }, [editForm.departmentId, editForm.roleId, onboardingOptions.roles]);

  useEffect(() => {
    if (!visibleSections.includes(activeSection)) {
      setActiveSection("Basic Info");
    }
  }, [activeSection, visibleSections]);

  useEffect(() => {
    if (!companyAccount || activeSection !== "Wallet" || !employeeId) {
      return;
    }

    let active = true;
    setWalletLoading(true);
    setWalletError("");
    getAdminEmployeeWallet(employeeId)
      .then((payload) => {
        if (active) setWalletDetails(payload);
      })
      .catch((requestError) => {
        if (!active) return;
        const message = requestError?.response?.data?.message || "Unable to load employee wallet.";
        setWalletError(Array.isArray(message) ? message.join(", ") : message);
      })
      .finally(() => {
        if (active) setWalletLoading(false);
      });

    return () => {
      active = false;
    };
  }, [activeSection, companyAccount, employeeId]);

  const openToolManager = (mode = "assign") => {
    setToolManagerMode(mode);
    setSelectedToolIds(mode === "withdraw" ? assignedToolIds : []);
    setToolsError("");
    setToolsSuccess("");
    setToolManagerOpen(true);
  };

  const closeToolManager = () => {
    setToolManagerOpen(false);
    setToolManagerMode("assign");
    setSelectedToolIds([]);
  };

  const toggleToolSelection = (toolId) => {
    setSelectedToolIds((current) => (
      current.includes(toolId)
        ? current.filter((value) => value !== toolId)
        : [...current, toolId]
    ));
  };

  const handleToolMutation = async (event) => {
    event.preventDefault();

    if (!apiBaseUrl || !employee?.id) {
      setToolsError("Unable to update tools for this employee right now.");
      return;
    }

    if (selectedToolIds.length === 0) {
      setToolsError("Select at least one tool.");
      return;
    }

    try {
      setToolSubmitting(true);
      setToolsError("");

      const endpoint = toolManagerMode === "withdraw"
        ? `${apiBaseUrl}/organizations/employees/${encodeURIComponent(employee.id)}/tools/withdraw`
        : `${apiBaseUrl}/organizations/employees/${encodeURIComponent(employee.id)}/tools`;

      const response = await axios.patch(endpoint, { toolIds: selectedToolIds });
      const assignments = Array.isArray(response?.data?.assignments) ? response.data.assignments : [];
      const nextAssignedTools = assignments
        .filter((assignment) => assignment?.status === "Assigned")
        .map((assignment) => ({
          id: assignment.toolId,
          name: assignment.toolName,
          description: assignment.toolDescription || "",
        }));
      const nextToolNames = nextAssignedTools.map((tool) => tool.name).filter(Boolean);

      setEmployee((prev) => (
        prev
          ? {
              ...prev,
              toolAssignments: assignments.map((assignment) => ({
                id: assignment.id,
                status: assignment.status,
                assignedAt: assignment.assignedAt,
                withdrawnAt: assignment.withdrawnAt,
                tool: {
                  id: assignment.toolId,
                  name: assignment.toolName,
                  description: assignment.toolDescription || "",
                },
              })),
              toolsAssigned: nextToolNames.join(", "),
            }
          : prev
      ));
      setAssignedTools(nextToolNames);
      setToolsAssigned(nextToolNames.join(", "));
      setToolsSuccess(toolManagerMode === "withdraw" ? "Tools withdrawn successfully." : "Tools assigned successfully.");
      setToolManagerOpen(false);
      setSelectedToolIds([]);
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Unable to update tools for this employee.";
      setToolsError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setToolSubmitting(false);
    }
  };

  const handleOffboard = async () => {
    if (!apiBaseUrl || !employee?.id || employee?.isOffboarded) {
      return;
    }

    try {
      setOffboardLoading(true);
      setOffboardError("");
      setOffboardSuccess("");

      const payload = {
        noticePeriod: noticePeriod.trim(),
        toolsAssigned: toolsAssigned.trim(),
      };

      await axios.patch(`${apiBaseUrl}/organizations/employees/${encodeURIComponent(employee.id)}/offboard`, payload);

      setEmployee((prev) =>
        prev
          ? {
              ...prev,
              isOffboarded: true,
              status: "Offboarded",
              offboardingNoticePeriod: payload.noticePeriod,
              offboardingToolsAssigned: payload.toolsAssigned,
              offboardedAt: new Date().toISOString().slice(0, 10),
            }
          : prev,
      );

      setOffboardSuccess("Employee was offboarded successfully.");
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Unable to offboard employee.";
      setOffboardError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setOffboardLoading(false);
    }
  };

  const handleReOnboard = async () => {
    if (!apiBaseUrl || !employee?.id || !employee?.isOffboarded) {
      return;
    }

    try {
      setOffboardLoading(true);
      setOffboardError("");
      setOffboardSuccess("");

      await axios.patch(`${apiBaseUrl}/organizations/employees/${encodeURIComponent(employee.id)}/reonboard`);

      setEmployee((prev) =>
        prev
          ? {
              ...prev,
              isOffboarded: false,
              status: "Approved",
              offboardedAt: "",
              offboardingNoticePeriod: "",
              offboardingToolsAssigned: "",
            }
          : prev,
      );

      setNoticePeriod("");
      setToolsAssigned("");
      setOffboardSuccess("Employee was re-onboarded successfully.");
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Unable to re-onboard employee.";
      setOffboardError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setOffboardLoading(false);
    }
  };

  const handleDeleteOffboarded = async () => {
    if (!apiBaseUrl || !employee?.id || !employee?.isOffboarded) {
      return;
    }

    try {
      setOffboardLoading(true);
      setOffboardError("");
      setOffboardSuccess("");

      await axios.delete(`${apiBaseUrl}/organizations/employees/${encodeURIComponent(employee.id)}/offboarded`);
      navigate("/employee");
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Unable to delete offboarded employee.";
      setOffboardError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setOffboardLoading(false);
    }
  };

  const handleUpdateEmployee = async () => {
    if (!apiBaseUrl || !employee?.id) {
      return;
    }

    try {
      setUpdateLoading(true);
      setUpdateError("");
      setUpdateSuccess("");

      const payload = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        officialEmail: editForm.officialEmail,
        personalEmail: editForm.personalEmail,
        phone: editForm.phone,
        employmentType: editForm.employmentType,
        gender: editForm.gender,
        maritalStatus: editForm.maritalStatus,
        stateOrigin: editForm.stateOrigin,
        stateResidence: editForm.stateResidence,
        lga: editForm.lga,
        residentialAddress: editForm.residentialAddress,
        departmentId: editForm.departmentId,
        departmentIds: [...new Set([editForm.departmentId, ...(editForm.departmentIds || [])].filter(Boolean))],
        roleId: editForm.roleId,
        levelId: editForm.levelId,
        positionId: editForm.positionId,
        branchId: editForm.branchId,
        dateJoined: editForm.dateJoined,
        employeeStatus: employee?.status === "Pending" ? "Approved" : editForm.employeeStatus,
      };

      await axios.patch(`${apiBaseUrl}/organizations/employees/${encodeURIComponent(employee.id)}`, payload);

      setEmployee((prev) =>
        prev
          ? {
              ...prev,
              ...payload,
              status: payload.employeeStatus,
              fullName: `${payload.firstName || ""} ${payload.lastName || ""}`.trim(),
              firstName: payload.firstName,
              lastName: payload.lastName,
              department: onboardingOptions.departments.find((item) => item.id === payload.departmentId) || prev.department,
              departments: payload.departmentIds.map((id) => ({
                ...(onboardingOptions.departments.find((item) => item.id === id) || { id, name: id }),
                isPrimary: id === payload.departmentId,
              })),
              role: onboardingOptions.roles.find((item) => item.id === payload.roleId) || prev.role,
              level: onboardingOptions.levels.find((item) => item.id === payload.levelId) || prev.level,
              position: onboardingOptions.positions.find((item) => item.id === payload.positionId) || null,
              branch: onboardingOptions.branches.find((item) => item.id === payload.branchId) || prev.branch,
            }
          : prev,
      );

      setUpdateSuccess("Employee details updated successfully.");
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Unable to update employee details.";
      setUpdateError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return <FetchLoadingState message="Loading employee details..." className="min-h-[60vh] font-Outfit" />;
  }

  if (error) {
    return (
      <div className="p-6 font-Outfit">
        <p className="text-sm text-red-600" role="alert">{error}</p>
        <button className="mt-4 rounded-lg border border-[#1234CD] px-4 py-2 text-[#1234CD]" onClick={() => navigate(-1)}>
          Go back
        </button>
      </div>
    );
  }

  if (!employee) {
    return null;
  }

  const basicInfoContent = (
    <div className="space-y-3">
      {labelValue("Employee ID", employee.employeeNumber)}
      {labelValue("Official Email", employee.officialEmail)}
      {labelValue("Personal Email", employee.personalEmail)}
      {labelValue("Phone", employee.phone)}
      {labelValue("Gender", employee.gender)}
      {labelValue("Marital Status", employee.maritalStatus)}
      {labelValue("Date of Birth", formatDate(employee.dateOfBirth))}
      {labelValue("State of Origin", employee.stateOrigin)}
      {labelValue("State of Residence", employee.stateResidence)}
      {labelValue("LGA", employee.lga)}
      {labelValue("Residential Address", employee.residentialAddress)}
      {labelValue("Created", formatDate(employee.createdAt))}
      {labelValue("Last Updated", formatDate(employee.updatedAt))}
    </div>
  );

  const organizationContent = (
    <div className="space-y-3">
      {labelValue("Primary Department", employee.department?.name)}
      {labelValue(
        "All Departments",
        Array.isArray(employee.departments) && employee.departments.length
          ? employee.departments.map((department) => `${department.name}${department.isPrimary ? " (Primary)" : ""}`).join(", ")
          : employee.department?.name,
      )}
      {labelValue("Role", employee.role?.name)}
      {labelValue("Level", employee.level?.name)}
      {labelValue("Position", employee.position?.name)}
      {labelValue("Branch", employee.branch?.name)}
      {labelValue("Date Joined", formatDate(employee.dateJoined))}
      {labelValue("Employment Type", employee.employmentType)}
      {labelValue("Status", employeeStatus)}
      {labelValue("Offboarded At", employee.offboardedAt ? formatDate(employee.offboardedAt) : "-")}
      {labelValue("Notice Period", employee.offboardingNoticePeriod)}
      {labelValue(
        "Tools Assigned",
        assignedTools.length > 0
          ? assignedTools.join(", ")
          : employee.offboardingToolsAssigned || employee.toolsAssigned || "-",
      )}
    </div>
  );

  const toolsContent = (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-[#071C50]">Assigned Tools</p>
          <p className="text-sm text-gray-500">Assign, withdraw, or reassign tools from this employee profile.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => openToolManager("assign")}
            disabled={toolsLoading || availableTools.length === 0}
            className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm text-white disabled:opacity-60"
          >
            Assign tools
          </button>
          <button
            type="button"
            onClick={() => openToolManager("withdraw")}
            disabled={toolsLoading || assignedToolIds.length === 0}
            className="rounded-lg border border-[#1234CD] px-4 py-2 text-sm text-[#1234CD] disabled:opacity-60"
          >
            Withdraw tools
          </button>
        </div>
      </div>

      {toolsLoading ? <p className="text-sm text-gray-500">Loading available tools...</p> : null}
      {toolsError ? <p className="text-sm text-red-600" role="alert">{toolsError}</p> : null}
      {toolsSuccess ? <p className="text-sm text-green-600">{toolsSuccess}</p> : null}

      <div className="rounded-2xl border border-gray-100 bg-[#FAFBFF] p-4">
        {assignedToolLabels.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {assignedToolLabels.map((toolName) => (
              <span key={toolName} className="rounded-full bg-white px-3 py-1 text-sm font-medium text-[#1234CD] shadow-sm">
                {toolName}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No tools are currently assigned.</p>
        )}
      </div>
    </div>
  );

  const idCardContent = <StaffIdCard employee={employee} photo={profilePhoto} />;

  const educationContent = renderCollection(employee.educationRecords, "No education records.", (item, index) => (
    <div className="space-y-2">
      {labelValue("Record", `${index + 1}`)}
      {labelValue("School", normalizeText(item.school))}
      {labelValue("Degree", normalizeText(item.degree))}
      {labelValue("Course", normalizeText(item.course))}
      {labelValue("Qualification Class", normalizeText(item.qualificationClass))}
      {labelValue("Start Year", normalizeText(item.startYear))}
      {labelValue("End Year", normalizeText(item.endYear))}
    </div>
  ));

  const experienceContent = renderCollection(employee.experienceRecords, "No experience records.", (item, index) => (
    <div className="space-y-2">
      {labelValue("Record", `${index + 1}`)}
      {labelValue("Company", normalizeText(item.company))}
      {labelValue("Position", normalizeText(item.position))}
      {labelValue("Employment Type", normalizeText(item.employmentType))}
      {labelValue("Start Date", formatDate(item.start))}
      {labelValue("End Date", formatDate(item.end))}
      {labelValue("Description", normalizeText(item.description))}
      {labelValue("Portfolio Link", normalizeText(item.portfolioLink))}
    </div>
  ));

  const awardsContent = renderCollection(employee.awardsRecords, "No award or certification records.", (item, index) => (
    <div className="space-y-2">
      {labelValue("Record", `${index + 1}`)}
      {labelValue("Award Name", normalizeText(item.nameOfAward))}
      {labelValue("Issued By", normalizeText(item.issuedBy))}
      {labelValue("Award Date", formatDate(item.awardDate))}
      {labelValue("Award Expiry", formatDate(item.expiryDate))}
      {labelValue("Certification", normalizeText(item.certification))}
      {labelValue("Certification Body", normalizeText(item.certificationBody))}
      {labelValue("Certification Expiry", formatDate(item.certificateExpiryDate))}
    </div>
  ));

  const benefitsContent = (
    <div className="space-y-3">
      {labelValue("Pension Provider", employee.pensionProvider)}
      {labelValue("Pension Number", employee.pensionNumber)}
      {labelValue("On HMO", employee.hmo)}
      {labelValue("HMO ID", employee.hmoId)}
      {labelValue("HMO Number", employee.hmoNumber)}
      {labelValue("HMO Status", employee.hmoStatus)}
      {labelValue("HMO Plan", employee.hmoPlan)}
      {labelValue("HMO Hospital", employee.hmoHospital)}
    </div>
  );

  const walletCurrency = walletDetails?.settings?.currency || "NGN";
  const employeeWallet = walletDetails?.wallet || {};
  const walletContent = walletLoading ? (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-8 text-center text-sm text-slate-500">
      Loading wallet details...
    </div>
  ) : walletError ? (
    <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-600" role="alert">{walletError}</div>
  ) : walletDetails ? (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          ["Points Balance", `${(employeeWallet.pointsBalance || 0).toLocaleString()} pts`, "bg-[#EEF3FF] text-[#1234CD]"],
          ["Available Funds", formatMoney(employeeWallet.cashBalanceKobo, walletCurrency), "bg-emerald-50 text-emerald-600"],
          ["Lifetime Earned", `${(employeeWallet.lifetimePointsEarned || 0).toLocaleString()} pts`, "bg-violet-50 text-violet-600"],
          ["Lifetime Deducted", `${(employeeWallet.lifetimePointsLost || 0).toLocaleString()} pts`, "bg-amber-50 text-amber-600"],
        ].map(([label, value, tone]) => (
          <div key={label} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
            <span className={`inline-flex rounded-lg px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${tone}`}>{label}</span>
            <p className="mt-3 break-words text-xl font-bold text-[#071C50]">{value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <h3 className="font-semibold text-[#071C50]">Wallet Summary</h3>
          <div className="mt-3 space-y-2 text-sm">
            {labelValue("Lifetime Funds Converted", formatMoney(employeeWallet.lifetimeCashKobo, walletCurrency))}
            {labelValue("Point Conversion Rate", `1 point = ${formatMoney(walletDetails.settings?.koboPerPoint, walletCurrency)}`)}
            {labelValue("Minimum Conversion", `${walletDetails.settings?.minimumConversionPoints || 0} points`)}
            {labelValue("Minimum Withdrawal", formatMoney(walletDetails.settings?.minimumWithdrawalKobo, walletCurrency))}
            {labelValue("Withdrawals", walletDetails.settings?.withdrawalsEnabled ? "Enabled" : "Disabled")}
          </div>
        </section>
        <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <h3 className="font-semibold text-[#071C50]">Points by Activity</h3>
          <div className="mt-3 max-h-64 space-y-2 overflow-y-auto">
            {walletDetails.activityBreakdown?.length ? walletDetails.activityBreakdown.map((item) => (
              <div key={item.eventKey} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 p-3">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-slate-700">{item.name}</p><p className="text-xs text-slate-400">{item.category} · {item.occurrences} occurrence{item.occurrences === 1 ? "" : "s"}</p></div>
                <p className={`shrink-0 text-sm font-bold ${item.totalPoints >= 0 ? "text-emerald-600" : "text-red-600"}`}>{item.totalPoints > 0 ? "+" : ""}{item.totalPoints} pts</p>
              </div>
            )) : <p className="py-6 text-center text-sm text-slate-500">No rewarded activity yet.</p>}
          </div>
        </section>
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-4"><h3 className="font-semibold text-[#071C50]">Recent Wallet Transactions</h3></div>
        <div className="max-h-[420px] divide-y divide-slate-100 overflow-y-auto">
          {walletDetails.transactions?.length ? walletDetails.transactions.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 p-4">
              <div className="min-w-0"><p className="truncate text-sm font-medium text-slate-700">{item.description}</p><p className="text-xs text-slate-400">{new Date(item.createdAt).toLocaleString()} · {item.type.replace(/_/g, " ")}</p></div>
              <div className="shrink-0 text-right">{item.pointsDelta ? <p className={`text-sm font-bold ${item.pointsDelta > 0 ? "text-emerald-600" : "text-red-600"}`}>{item.pointsDelta > 0 ? "+" : ""}{item.pointsDelta} pts</p> : null}{item.cashDeltaKobo ? <p className={`text-xs font-semibold ${item.cashDeltaKobo > 0 ? "text-emerald-600" : "text-red-600"}`}>{item.cashDeltaKobo > 0 ? "+" : "-"}{formatMoney(Math.abs(item.cashDeltaKobo), walletCurrency)}</p> : null}</div>
            </div>
          )) : <p className="p-8 text-center text-sm text-slate-500">No wallet transactions yet.</p>}
        </div>
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4"><h3 className="font-semibold text-[#071C50]">Point Conversions</h3></div>
          <div className="max-h-72 divide-y divide-slate-100 overflow-y-auto">
            {walletDetails.conversions?.length ? walletDetails.conversions.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-4"><div><p className="text-sm font-medium text-slate-700">{item.points.toLocaleString()} points</p><p className="text-xs text-slate-400">{new Date(item.createdAt).toLocaleDateString()}</p></div><p className="font-bold text-emerald-600">{formatMoney(item.cashKobo, walletCurrency)}</p></div>
            )) : <p className="p-6 text-center text-sm text-slate-500">No conversions yet.</p>}
          </div>
        </section>
        <section className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4"><h3 className="font-semibold text-[#071C50]">Withdrawal History</h3></div>
          <div className="max-h-72 divide-y divide-slate-100 overflow-y-auto">
            {walletDetails.withdrawals?.length ? walletDetails.withdrawals.map((item) => (
              <div key={item.id} className="p-4"><div className="flex items-center justify-between"><p className="font-bold text-[#071C50]">{formatMoney(item.amountKobo, walletCurrency)}</p><span className={`rounded-full px-2 py-1 text-[10px] font-bold ${item.status === "REJECTED" ? "bg-red-50 text-red-600" : item.status === "PENDING" ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>{item.status}</span></div><p className="mt-1 text-xs text-slate-500">{item.bankName} · {item.accountName} · ••••{item.accountNumber.slice(-4)}</p><p className="mt-1 text-xs text-slate-400">{new Date(item.createdAt).toLocaleString()}</p>{item.reviewNote ? <p className="mt-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">{item.reviewNote}</p> : null}</div>
            )) : <p className="p-6 text-center text-sm text-slate-500">No withdrawals yet.</p>}
          </div>
        </section>
      </div>
    </div>
  ) : (
    <p className="text-sm text-slate-500">Open this section to load wallet details.</p>
  );

  const actionsContent = (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-lg font-semibold text-[#071C50]">Update Staff Details</p>
          <button
            type="button"
            onClick={() => setShowEditForm((prev) => !prev)}
            className="rounded-lg border border-[#1234CD] px-4 py-2 text-sm text-[#1234CD]"
          >
            {showEditForm ? "Close Edit" : "Edit Details"}
          </button>
        </div>

        {showEditForm ? (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-[#071C50]">Staff Status</span>
                <select
                  value={editForm.employeeStatus}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, employeeStatus: event.target.value }))}
                  className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-[#1234CD]"
                >
                  <option value="Approved">Approved</option>
                  <option value="Pending">Pending</option>
                  <option value="Offboarded">Offboarded</option>
                </select>
              </label>

              {["departmentId", "roleId", "levelId", "positionId", "branchId"].map((fieldKey) => {
                const fieldLabelMap = {
                  departmentId: "Primary Department",
                  roleId: "Role",
                  levelId: "Level",
                  positionId: "Position",
                  branchId: "Branch",
                };

                const optionsMap = {
                  departmentId: onboardingOptions.departments,
                  roleId: filteredRoleOptions,
                  levelId: onboardingOptions.levels,
                  positionId: onboardingOptions.positions,
                  branchId: onboardingOptions.branches,
                };

                const options = optionsMap[fieldKey] || [];

                return (
                  <label key={fieldKey} className="flex flex-col gap-1 text-sm">
                    <span className="font-medium text-[#071C50]">
                      {fieldLabelMap[fieldKey]}
                      {fieldKey === "positionId" ? " (Optional)" : ""}
                    </span>
                    <select
                      value={editForm[fieldKey] || ""}
                      onChange={(event) =>
                        setEditForm((prev) => ({
                          ...prev,
                          [fieldKey]: event.target.value,
                          ...(fieldKey === "departmentId" ? {
                            roleId: "",
                            departmentIds: event.target.value
                              ? [...new Set([event.target.value, ...(prev.departmentIds || [])])]
                              : (prev.departmentIds || []),
                          } : {}),
                        }))
                      }
                      disabled={fieldKey === "roleId" && !editForm.departmentId}
                      className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-[#1234CD]"
                    >
                      <option value="">{fieldKey === "roleId" && !editForm.departmentId ? "Choose department first" : `Select ${fieldLabelMap[fieldKey]}`}</option>
                      {options.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}

              <fieldset className="md:col-span-2 rounded-xl border border-gray-200 p-4">
                <legend className="px-1 text-sm font-semibold text-[#071C50]">All department assignments</legend>
                <p className="mb-3 text-xs text-gray-500">Select every department this employee belongs to. Changing the primary department updates the main role and reporting line; every secondary department HOD receives the same staff-management access.</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {(onboardingOptions.departments || []).map((department) => {
                    const isPrimary = department.id === editForm.departmentId;
                    const checked = isPrimary || (editForm.departmentIds || []).includes(department.id);
                    return (
                      <label key={department.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isPrimary}
                          onChange={() => setEditForm((prev) => ({
                            ...prev,
                            departmentIds: (prev.departmentIds || []).includes(department.id)
                              ? prev.departmentIds.filter((id) => id !== department.id)
                              : [...(prev.departmentIds || []), department.id],
                          }))}
                        />
                        <span className="min-w-0 flex-1 truncate text-sm">{department.name}</span>
                        {isPrimary ? <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-[#1234CD]">Primary</span> : null}
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-[#071C50]">Date Joined</span>
                <input
                  type="date"
                  value={editForm.dateJoined || ""}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, dateJoined: event.target.value }))}
                  className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-[#1234CD]"
                />
              </label>

              {[
                ["firstName", "First Name"],
                ["lastName", "Last Name"],
                ["officialEmail", "Official Email"],
                ["personalEmail", "Personal Email"],
                ["phone", "Phone Number"],
                ["employmentType", "Employment Type"],
                ["gender", "Gender"],
                ["maritalStatus", "Marital Status"],
                ["stateOrigin", "State of Origin"],
                ["stateResidence", "State of Residence"],
                ["lga", "LGA"],
                ["residentialAddress", "Residential Address"],
              ].map(([key, label]) => (
                <label key={key} className="flex flex-col gap-1 text-sm">
                  <span className="font-medium text-[#071C50]">{label}</span>
                  <input
                    value={editForm[key] || ""}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, [key]: event.target.value }))}
                    className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-[#1234CD]"
                  />
                </label>
              ))}
            </div>

            {updateError ? <p className="text-sm text-red-600" role="alert">{updateError}</p> : null}
            {updateSuccess ? <p className="text-sm text-green-600">{updateSuccess}</p> : null}

            <button
              onClick={handleUpdateEmployee}
              disabled={updateLoading}
              className="rounded-lg bg-[#1234CD] px-5 py-2 text-white disabled:opacity-60"
            >
              {updateLoading
                ? "Saving..."
                : employee?.status === "Pending"
                  ? "Complete Organization & Approve"
                  : "Save Changes"}
            </button>
          </>
        ) : null}
      </div>

      <div className="space-y-3">
        <p className="text-lg font-semibold text-[#071C50]">Offboarding Controls</p>

        {employee.isOffboarded ? (
          <>
            <p className="text-sm text-gray-600">
              This employee is already offboarded {employee.offboardedAt ? `on ${formatDate(employee.offboardedAt)}` : ""}.
            </p>

            {offboardError ? <p className="text-sm text-red-600" role="alert">{offboardError}</p> : null}
            {offboardSuccess ? <p className="text-sm text-green-600">{offboardSuccess}</p> : null}

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleReOnboard}
                disabled={offboardLoading}
                className="rounded-lg bg-[#1234CD] px-5 py-2 text-white disabled:opacity-60"
              >
                {offboardLoading ? "Processing..." : "Re-onboard Staff"}
              </button>

              <button
                onClick={handleDeleteOffboarded}
                disabled={offboardLoading}
                className="rounded-lg bg-[#FF383B] px-5 py-2 text-white disabled:opacity-60"
              >
                {offboardLoading ? "Deleting..." : "Delete Offboarded Staff"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-[#071C50]">Notice Period</span>
                <input
                  value={noticePeriod}
                  onChange={(event) => setNoticePeriod(event.target.value)}
                  placeholder="e.g. 30 days"
                  className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-[#1234CD]"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-[#071C50]">Tools Assigned</span>
                <input
                  value={toolsAssigned}
                  onChange={(event) => setToolsAssigned(event.target.value)}
                  placeholder="e.g. Laptop, Access Card"
                  className="rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-[#1234CD]"
                />
              </label>
            </div>

            {offboardError ? <p className="text-sm text-red-600" role="alert">{offboardError}</p> : null}
            {offboardSuccess ? <p className="text-sm text-green-600">{offboardSuccess}</p> : null}

            <button
              onClick={handleOffboard}
              disabled={offboardLoading}
              className="rounded-lg bg-[#FF383B] px-5 py-2 text-white disabled:opacity-60"
            >
              {offboardLoading ? "Offboarding..." : "Offboard Employee"}
            </button>
          </>
        )}
      </div>
    </div>
  );

  const sectionContentMap = {
    "Basic Info": basicInfoContent,
    Organization: organizationContent,
    Tools: toolsContent,
    "ID Card": idCardContent,
    Education: educationContent,
    Experience: experienceContent,
    "Awards & Certification": awardsContent,
    "Employment & Benefits": benefitsContent,
    Wallet: walletContent,
    Actions: actionsContent,
  };

  return (
    <div className="p-4 font-Outfit sm:p-6">
      <h1 className="mb-2 text-[20px] font-[700] leading-[30px] text-[#400167]">{employee.fullName || "Employee Details"}</h1>
      <p className="mb-6 flex items-center gap-2 text-sm text-[#6B7280]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#1234CD] text-[#1234CD]"
          aria-label="Go back"
        >
          &larr;
        </button>
        <button type="button" onClick={() => navigate("/employee")} className="text-[#1234CD] hover:underline">
          Employees
        </button>
        <span className="mx-2">/</span>
        Employee Details
      </p>

      <div className="mb-5 rounded-xl bg-[#F4F4F8] p-4 sm:mb-6 sm:p-5">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
          <div className="flex min-w-0 flex-col items-center gap-4 text-center sm:flex-row sm:gap-5 sm:text-left">
            <img src={profilePhoto} alt="Profile" className="h-24 w-24 rounded-full object-cover sm:h-[100px] sm:w-[100px]" />
            <div className="min-w-0 max-w-full">
              <h2 className="break-words text-2xl font-semibold text-[#071C50] sm:text-[34px]">{employee.fullName || "Unnamed employee"}</h2>
              <p className="mt-1 break-all text-sm text-[#071C50] sm:break-normal">{employee.officialEmail || employee.personalEmail || "-"}</p>
              <p className="text-sm text-[#071C50]">{employee.phone || "-"}</p>
              <p className="mt-1 text-[#071C50] text-sm">Employee ID: {employee.employeeNumber || "-"}</p>
              <p className="mt-1 text-[#071C50] text-sm">
                Level:{" "}
                <span className="rounded-md bg-[#DDEAFB] px-2 py-1 text-sm">{employee.level?.name || "-"}</span>
              </p>
              {companyAccount ? <div className="pt-4 text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setActiveSection("Actions");
                    setShowEditForm(true);
                  }}
                  className="mr-3 text-[#12CD18] hover:underline"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveSection("Actions");
                  }}
                  className="text-[#FF383B] hover:underline"
                >
                  {employee.isOffboarded ? "Offboarded" : "Offboard User"}
                </button>
              </div> : null}
            </div>
          </div>

          <div className="rounded-full border-8 border-[#D1D5DB] p-6 text-center">
            <p className={`text-xl font-semibold ${statusColor}`}>{employeeStatus}</p>
            <p className="text-sm text-gray-500">status</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 md:flex-row">
        <div className="sticky top-0 z-20 -mx-4 border-y border-slate-100 bg-white/95 px-4 py-3 shadow-sm backdrop-blur md:hidden">
          <div className="grid grid-cols-[auto,1fr,auto] gap-2">
            <button type="button" disabled={activeSection === visibleSections[0]} onClick={() => setActiveSection(visibleSections[Math.max(0, visibleSections.indexOf(activeSection) - 1)])} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#1234CD] disabled:opacity-35" aria-label="Previous employee section">&larr;</button>
            <select value={activeSection} onChange={(event) => setActiveSection(event.target.value)} className="min-w-0 rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 text-sm font-semibold text-[#071C50]">
              {visibleSections.map((section) => <option key={section} value={section}>{section}</option>)}
            </select>
            <button type="button" disabled={activeSection === visibleSections[visibleSections.length - 1]} onClick={() => setActiveSection(visibleSections[Math.min(visibleSections.length - 1, visibleSections.indexOf(activeSection) + 1)])} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#1234CD] disabled:opacity-35" aria-label="Next employee section">&rarr;</button>
          </div>
        </div>

        <div className="hidden md:block md:w-[220px] md:border-r md:pr-4">
          {visibleSections.map((section) => (
            <button
              key={section}
              type="button"
              onClick={() => setActiveSection(section)}
              className={`block w-full px-4 py-2 text-left text-base ${
                activeSection === section
                  ? "border-b-2 border-[#1234CD] font-medium text-black"
                  : "text-[#071C50] hover:bg-gray-50"
              }`}
            >
              {section}
            </button>
          ))}
        </div>

        <div className="min-w-0 flex-1 md:px-4">
          <h2 className="mb-4 text-2xl font-semibold text-[#071C50] sm:text-[34px]">{activeSection}</h2>
          <div className="text-[#071C50]">{sectionContentMap[activeSection]}</div>
        </div>
      </div>

      <InviteModal
        isOpen={toolManagerOpen}
        onClose={closeToolManager}
      >
        <div className="my-6">
          <p className="text-2xl font-semibold text-black font-Outfit">
            {toolManagerMode === "withdraw" ? "Withdraw Tools" : "Assign Tools"}
          </p>
          <p className="mt-2 text-sm text-gray-500">
            {toolManagerMode === "withdraw"
              ? "Choose one or more currently assigned tools to withdraw from this employee."
              : "Choose tools from the organization inventory to assign to this employee."}
          </p>

          <form className="mt-8 space-y-5" onSubmit={handleToolMutation}>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-700">Select tools</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 max-h-[360px] overflow-y-auto pr-1">
                {availableTools
                  .filter((tool) => (toolManagerMode === "withdraw" ? assignedToolIds.includes(tool.id) : true))
                  .map((tool) => {
                    const isAssigned = assignedToolIds.includes(tool.id);

                    return (
                      <label key={tool.id} className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                        <input
                          type="checkbox"
                          checked={selectedToolIds.includes(tool.id)}
                          onChange={() => toggleToolSelection(tool.id)}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1234CD] focus:ring-[#1234CD]"
                        />
                        <div>
                          <p className="font-medium text-[#071C50]">{tool.name}</p>
                          <p className="text-xs text-slate-500">{tool.description || "No description available"}</p>
                          <p className="mt-1 text-xs text-slate-400">{tool.isActive ? "Active" : "Inactive"}{isAssigned ? " • Currently assigned" : ""}</p>
                        </div>
                      </label>
                    );
                  })}
              </div>
              {availableTools.length === 0 ? <p className="mt-3 text-sm text-slate-500">Create tools in company settings before assigning them here.</p> : null}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={closeToolManager} className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                Cancel
              </button>
              <button type="submit" disabled={toolSubmitting} className="rounded-xl bg-[#1234CD] px-5 py-3 text-sm text-white disabled:opacity-60">
                {toolSubmitting ? "Saving..." : toolManagerMode === "withdraw" ? "Withdraw Selected" : "Assign Selected"}
              </button>
            </div>
          </form>
        </div>
      </InviteModal>
    </div>
  );
};

export default EmployeesPage;
